import React, { useState, useRef, useEffect, useCallback } from 'react';
import './UploadModal.css';
import { API_BASE_URL } from '../../../../API/api';
import { Auth } from 'aws-amplify';
import { useUploads } from '../../../../contexts/UploadsContext';
import { useDownloads } from '../../../../contexts/DownloadsContext';
import {
  uploadMultipart,
  completeMultipart,
  initMultipart,
  initDrive,
  confirmDrive,
} from '../../../../utils/uploadStrategies';
import { uploadVisualizationFile } from '../../../../utils/uploadVisualization';
import { saveSession, updateSession, deleteSession } from '../../../../utils/uploadSessionsDB';
import { findNameConflicts, pickAvailableName, normalizeNameForCompare } from '../../../../utils/nameUtils';
import { parseLasHeaderFromFile, detectUnits, isLasLaz } from '../../../../utils/lasHeader';
import ReprojectCrsModal from './ReprojectCrsModal';

const UploadModal = ({
  isOpen,
  onClose,
  onUpload,
  onFileAdded,
  initialFiles = [],
  projectId,
  projectType,
  currentFolderId,
  currentFolderGoogleDriveId,
  currentFolderItems = [],   // NEW: list of {id, googleDriveId, name, isFolder}
  projectName,
  currentFolderName,
  warnOnExistingRouteXml = false,
  accept = '',
  sourceStorage = 'drive',
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadSpeed, setUploadSpeed] = useState({}); // { [index]: mbps }
  // Byte-count samples per file for computing MB/s. Kept in a ref so the
  // progress callbacks don't re-render the whole list on every byte update.
  const speedTrackerRef = useRef({}); // { [index]: { lastBytes, lastTime, fileSize } }
  // Indices the user explicitly cancelled so the upload catch block knows
  // not to overwrite status='cancelled' with status='error'.
  const cancelledFilesRef = useRef(new Set());
  // Indices currently paused. Between multipart parts the upload function
  // awaits a resume signal via pauseWaitersRef[index].
  const pausedFilesRef = useRef(new Set());
  const pauseWaitersRef = useRef({}); // { [index]: [resolveFn, ...] }
  const [uploading, setUploading] = useState(false);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [isDragActive, setIsDragActive] = useState(false);
  const [existingXmlConflictState, setExistingXmlConflictState] = useState(null);
  const [nameConflictState, setNameConflictState] = useState(null);
  // LAS/LAZ reprojection preflight (S3-source projects only). When source CRS
  // can't be auto-detected, the modal collects it. Decisions are keyed by
  // fileKey and consumed in uploadMultipartFile → completeMultipart.
  const [reprojectModal, setReprojectModal] = useState(null);
  const reprojectDecisionsRef = useRef({}); // { [fileKey]: { sourceCrs, targetWkt } }
  // Shape: {
  //   rows: [{ fileKey, fileName, existingDriveFileIds: [string], action: 'rename'|'replace'|'skip', newName: string }],
  //   pendingUploadEntries: [{ file, fileKey, ... }],   // the batch as it was before the dialog opened
  // }
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancellingUpload, setCancellingUpload] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const fileInputRef = useRef(null);
  const { addUpload, updateUpload, removeUpload: removeContextUpload, openPopup: openUploadsPopup, closePopup: closeUploadsPopup, registerCancelHandler, unregisterCancelHandler, registerExpandHandler, unregisterExpandHandler } = useUploads();
  const { closePopup: closeDownloadsPopup } = useDownloads();
  const activeUploadIdRef = useRef(null);
  const handleConfirmCancelRef = useRef(null);
  const activeXhrsRef = useRef([]);
  const activeWorkersRef = useRef([]);
  const uploadCancelledRef = useRef(false);
  const uploadedDriveFilesRef = useRef([]);
  const sessionLocalIdsRef = useRef({});                // { [index]: localId }
  const debouncedUpdateRef = useRef({});                // { [localId]: { timer, pending } }

  const scheduleUpdate = (localId, patch) => {
    const entry = debouncedUpdateRef.current[localId] || { pending: {} };
    entry.pending = { ...entry.pending, ...patch };
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = setTimeout(() => {
      updateSession(localId, entry.pending).catch(() => {});
      entry.pending = {};
      entry.timer = null;
    }, 500);
    debouncedUpdateRef.current[localId] = entry;
  };

  const newLocalId = () => (
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  const getFileKey = (file) => `${file.name}__${file.size}__${file.lastModified}`;

  useEffect(() => {
    if (!isOpen || !Array.isArray(initialFiles) || initialFiles.length === 0) return;

    const unique = [...new Map(initialFiles.map((file) => [getFileKey(file), file])).values()];
    setSelectedFiles(unique);
    const nextProgress = {};
    const nextStatus = {};
    unique.forEach((_, index) => {
      nextProgress[index] = 0;
      nextStatus[index] = 'pending';
    });
    setUploadProgress(nextProgress);
    setUploadStatus(nextStatus);
    setIsDragActive(false);
    setCheckingConflicts(false);
    setExistingXmlConflictState(null);
  }, [isOpen, initialFiles]);

  // Sync upload state to UploadsContext when minimized
  useEffect(() => {
    if (!minimized || !activeUploadIdRef.current) return;
    const activeCount = selectedFiles.filter((_, i) => uploadStatus[i] === 'uploading' || uploadStatus[i] === 'finalizing').length;
    const errorCount = selectedFiles.filter((_, i) => uploadStatus[i] === 'error').length;
    const successCount = selectedFiles.filter((_, i) => uploadStatus[i] === 'success').length;
    let status = 'uploading';
    if (!uploading && activeCount === 0) {
      status = errorCount > 0 ? 'error' : 'completed';
    }
    updateUpload(activeUploadIdRef.current, {
      files: selectedFiles,
      fileStatuses: { ...uploadStatus },
      fileProgress: { ...uploadProgress },
      status,
    });
  }, [minimized, uploadStatus, uploadProgress, uploading, selectedFiles, updateUpload]);

  const handleMinimize = useCallback(() => {
    const id = addUpload({
      folderName: currentFolderName || projectName || 'Project Root',
      files: selectedFiles,
      fileStatuses: { ...uploadStatus },
      fileProgress: { ...uploadProgress },
      status: 'uploading',
    });
    activeUploadIdRef.current = id;
    registerCancelHandler(id, () => {
      handleConfirmCancelRef.current?.();
    });
    registerExpandHandler(id, () => {
      removeContextUpload(id);
      unregisterCancelHandler(id);
      unregisterExpandHandler(id);
      activeUploadIdRef.current = null;
      closeUploadsPopup();
      setMinimized(false);
    });
    setMinimized(true);
    closeDownloadsPopup();
    openUploadsPopup();
  }, [addUpload, openUploadsPopup, closeDownloadsPopup, registerCancelHandler, registerExpandHandler, removeContextUpload, unregisterCancelHandler, unregisterExpandHandler, selectedFiles, uploadStatus, uploadProgress, currentFolderName, projectName]);

  if (!isOpen) return null;

  const addFilesToSelection = (incomingFiles) => {
    if (!Array.isArray(incomingFiles) || incomingFiles.length === 0) return;

    setSelectedFiles((prevFiles) => {
      const existingKeys = new Set(prevFiles.map(getFileKey));
      const uniqueIncoming = incomingFiles.filter((file) => !existingKeys.has(getFileKey(file)));
      if (uniqueIncoming.length === 0) return prevFiles;

      const nextFiles = [...prevFiles, ...uniqueIncoming];
      const firstNewIndex = prevFiles.length;

      setUploadProgress((prev) => {
        const next = { ...prev };
        uniqueIncoming.forEach((_, offset) => {
          next[firstNewIndex + offset] = 0;
        });
        return next;
      });

      setUploadStatus((prev) => {
        const next = { ...prev };
        uniqueIncoming.forEach((_, offset) => {
          next[firstNewIndex + offset] = 'pending';
        });
        return next;
      });

      return nextFiles;
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    addFilesToSelection(files);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) {
      setIsDragActive(true);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (uploading) return;
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    addFilesToSelection(files);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setUploadProgress((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        const idx = Number(key);
        if (idx < index) next[idx] = value;
        if (idx > index) next[idx - 1] = value;
      });
      return next;
    });
    setUploadStatus((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        const idx = Number(key);
        if (idx < index) next[idx] = value;
        if (idx > index) next[idx - 1] = value;
      });
      return next;
    });
    setUploadSpeed((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        const idx = Number(key);
        if (idx < index) next[idx] = value;
        if (idx > index) next[idx - 1] = value;
      });
      return next;
    });
    // Re-index ref-backed maps and sets the same way as the state.
    const shiftSet = (set) => {
      const next = new Set();
      set.forEach((idx) => {
        if (idx < index) next.add(idx);
        else if (idx > index) next.add(idx - 1);
      });
      return next;
    };
    const shiftRecord = (record) => {
      const next = {};
      Object.entries(record).forEach(([key, value]) => {
        const idx = Number(key);
        if (idx < index) next[idx] = value;
        if (idx > index) next[idx - 1] = value;
      });
      return next;
    };
    cancelledFilesRef.current = shiftSet(cancelledFilesRef.current);
    pausedFilesRef.current = shiftSet(pausedFilesRef.current);
    pauseWaitersRef.current = shiftRecord(pauseWaitersRef.current);
    speedTrackerRef.current = shiftRecord(speedTrackerRef.current);
  };

  const getAuthToken = async () => {
    try {
      const session = await Auth.currentSession();
      if (session?.getIdToken) {
        const idToken = session.getIdToken().getJwtToken();
        if (idToken && String(idToken).trim()) {
          return String(idToken).trim();
        }
      }
    } catch {
      // Fall back to storage-based lookup.
    }

    const readFromStorage = (storage) => {
      if (!storage) return null;
      const directKeys = ['token', 'idToken', 'bimLiveAuthToken', 'cognito_token'];
      for (const key of directKeys) {
        const value = storage.getItem(key);
        if (value && String(value).trim()) return String(value).trim();
      }

      const authRaw = storage.getItem('auth');
      if (authRaw) {
        try {
          const parsed = JSON.parse(authRaw);
          if (typeof parsed === 'string' && parsed.trim()) return parsed.trim();
          if (parsed?.idToken && String(parsed.idToken).trim()) return String(parsed.idToken).trim();
          if (parsed?.token && String(parsed.token).trim()) return String(parsed.token).trim();
        } catch {
          // Ignore malformed auth JSON and continue fallback chain.
        }
      }
      return null;
    };

    return readFromStorage(sessionStorage) || readFromStorage(localStorage) || null;
  };

  const parseXhrJson = (xhr) => {
    try {
      return JSON.parse(xhr.responseText || '{}');
    } catch {
      return null;
    }
  };

  const buildExistingXmlConflictState = (conflicts, pendingUploadEntries) => {
    const xmlNamesMap = new Map();
    (Array.isArray(conflicts) ? conflicts : []).forEach((entry) => {
      (entry?.conflictingXmlNames || []).forEach((name) => {
        const normalized = String(name || '').trim();
        if (normalized) {
          xmlNamesMap.set(normalized.toLowerCase(), normalized);
        }
      });
    });

    return {
      conflicts: Array.isArray(conflicts) ? conflicts : [],
      conflictFileKeys: (Array.isArray(conflicts) ? conflicts : []).map((entry) => entry.fileKey),
      existingXmlNames: Array.from(xmlNamesMap.values()),
      pendingUploadEntries: Array.isArray(pendingUploadEntries) ? pendingUploadEntries : [],
    };
  };

  const buildNameConflictState = (conflictRows, pendingUploadEntries, currentFolderItemsList) => {
    // Pre-compute the suggested rename per row. The "existing names" set used
    // for suggestion includes the current folder listing.
    const existingNames = new Set();
    (currentFolderItemsList || []).forEach((it) => {
      if (it && it.name) existingNames.add(it.name);
    });
    // Track names that will be used by earlier rows opting into "Keep both"
    // so two same-batch dupes don't both pick the same (1) suffix.
    const reservedNames = new Set();
    const rows = conflictRows.map((c) => {
      const seedNames = new Set([...existingNames, ...reservedNames]);
      const suggested = pickAvailableName(c.name, seedNames);
      reservedNames.add(suggested);
      return {
        fileName: c.name,
        existingDriveFileIds: c.existingDriveFileIds || [],
        action: 'rename',           // default: Keep both
        resolved: false,
        newName: suggested,
      };
    });
    return { rows, pendingUploadEntries };
  };

  // Refresh-once-then-check. Returns true if the dialog state was set (and the
  // caller should bail out of the auto-flow), false if there were no conflicts.
  const inspectNameConflicts = async (uploadEntries) => {
    if (!uploadEntries || uploadEntries.length === 0) return false;
    // Lean on the in-memory listing — caller is responsible for refreshing
    // before opening the modal (FileExplorer already calls loadProjectFiles on
    // mount and on most actions). The 30s Drive cache TTL caps staleness.
    const items = currentFolderItems || [];
    const conflicts = findNameConflicts(
      uploadEntries.map((e) => e.file),
      items,
    );
    if (conflicts.length === 0) return false;
    const state = buildNameConflictState(conflicts, uploadEntries, items);
    setNameConflictState(state);
    return true;
  };

  const buildUploadError = (xhr) => {
    const maybeJson = parseXhrJson(xhr);
    if (maybeJson?.error) {
      return new Error(maybeJson.error);
    }

    const responseText = String(xhr.responseText || '').trim();
    const shortBody = responseText
      ? responseText.slice(0, 180).replace(/\s+/g, ' ')
      : '';
    const message = shortBody
      ? `Upload failed (${xhr.status}): ${shortBody}`
      : `Upload failed (${xhr.status})`;
    return new Error(message);
  };

  const LARGE_FILE_THRESHOLD = 50 * 1024 * 1024; // 50 MB — switch from direct POST to multipart

  // File extensions that need server-side processing (conflict detection, DXF conversion)
  const SERVER_PROCESSING_EXTENSIONS = new Set(['xml', 'dxf']);

  const getUploadStrategy = (file) => {
    // Visualization projects have a fully separate upload pipeline.
    // Routing here short-circuits all the Drive/RSA branches below so
    // those code paths can NEVER affect visualization uploads.
    if (projectType === 'visualization') return 'visualization';

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const needsServerProcessing = SERVER_PROCESSING_EXTENSIONS.has(ext);
    const hasDriveId = !!currentFolderGoogleDriveId;
    const isS3Source = sourceStorage === 's3';

    // Route every S3-source LAS/LAZ through multipart (even when small) so the
    // reprojection trigger lives in a single completion path (complete-multipart).
    if (isS3Source && (ext === 'las' || ext === 'laz')) return 'multipart';

    if (file.size < LARGE_FILE_THRESHOLD) return 'direct';

    if (isS3Source) return 'multipart';

    if (needsServerProcessing || !hasDriveId) return 'multipart';

    return 'drive-direct';
  };

  // ── Direct-to-Drive upload via Web Worker (for large LAS/LAZ files) ──
  const uploadFileDriveDirect = async (file, index, replaceExistingDriveFileIds) => {
    const token = await getAuthToken();
    const api = { apiBaseUrl: API_BASE_URL, projectId, authToken: token };

    // Phase 1: Get resumable URI from backend
    const { resumableUri, chunkSize } = await initDrive({
      file,
      googleDriveId: currentFolderGoogleDriveId,
      replaceExistingDriveFileIds,
      api,
    });

    const localId = newLocalId();
    sessionLocalIdsRef.current[index] = localId;

    await saveSession({
      localId,
      projectId: String(projectId),
      folderGoogleDriveId: currentFolderGoogleDriveId || null,
      folderName: currentFolderName || projectName || 'Project Root',
      sourceStorage,
      fileName: file.name,
      fileSize: file.size,
      lastModified: file.lastModified,
      mimeType: file.type || 'application/octet-stream',
      strategy: 'drive-direct',
      sessionId: { resumableUri, s3UploadId: null, s3Key: null, bucket: null },
      partSize: chunkSize,
      completedParts: [],
      bytesUploaded: 0,
      status: 'uploading',
      tabLockUntil: null,
      warnOnExistingRouteXml: false,
      existingXmlAction: '',
    }).catch(() => {});

    // Phase 2: Upload via Web Worker (chunked PUT directly to Google Drive)
    const driveFileId = await new Promise((resolve, reject) => {
      const worker = new Worker(
        new URL('./driveUploadWorker.js', import.meta.url)
      );
      activeWorkersRef.current.push(worker);

      worker.onmessage = (e) => {
        const msg = e.data;
        if (msg.type === 'progress') {
          // trackProgress also drives the MB/s sampler that feeds the speed/ETA
          // pills; the worker's percent is fine for the bar but skips that.
          trackProgress(index, msg.loaded, msg.total ?? file.size);
          scheduleUpdate(localId, { bytesUploaded: msg.loaded });
        } else if (msg.type === 'complete') {
          activeWorkersRef.current = activeWorkersRef.current.filter((w) => w !== worker);
          worker.terminate();
          resolve(msg.driveFileId);
        } else if (msg.type === 'error') {
          activeWorkersRef.current = activeWorkersRef.current.filter((w) => w !== worker);
          worker.terminate();
          reject(new Error(msg.message));
        }
      };

      worker.onerror = (err) => {
        activeWorkersRef.current = activeWorkersRef.current.filter((w) => w !== worker);
        worker.terminate();
        reject(new Error(err.message || 'Worker error'));
      };

      worker.postMessage({
        type: 'start',
        resumableUri,
        file,
        chunkSize,
        fileSize: file.size,
      });
    });

    // Phase 3: Confirm with backend (cache invalidation + bookkeeping)
    const result = await confirmDrive({
      driveFileId,
      file,
      googleDriveId: currentFolderGoogleDriveId,
      resumableUri,
      replaceExistingDriveFileIds,
      api,
    });
    if (!result) {
      // Upload succeeded but confirm failed — file is in Drive, just log warning
      console.warn('Drive upload confirm failed: null response');
    }

    await deleteSession(localId).catch(() => {});
    delete sessionLocalIdsRef.current[index];

    setUploadProgress((prev) => ({ ...prev, [index]: 100 }));
    return result || { success: true, driveFileId, file: { name: file.name, googleDriveId: driveFileId, google_drive_id: driveFileId, id: driveFileId } };
  };

  // ── Multipart upload for files >= 50 MB ──
  const uploadMultipartFile = async (file, index, existingXmlAction, replaceExistingDriveFileIds) => {
    const token = await getAuthToken();
    const api = { apiBaseUrl: API_BASE_URL, projectId, authToken: token };

    const { s3UploadId, s3Key, partSize, numParts, bucket } = await initMultipart({
      file, googleDriveId: currentFolderGoogleDriveId, replaceExistingDriveFileIds, api,
    });

    const localId = newLocalId();
    sessionLocalIdsRef.current[index] = localId;

    await saveSession({
      localId,
      projectId: String(projectId),
      folderGoogleDriveId: currentFolderGoogleDriveId || null,
      folderName: currentFolderName || projectName || 'Project Root',
      sourceStorage,
      fileName: file.name,
      fileSize: file.size,
      lastModified: file.lastModified,
      mimeType: file.type || 'application/octet-stream',
      strategy: 'multipart',
      sessionId: { s3UploadId, s3Key, bucket: bucket || null, resumableUri: null },
      partSize,
      completedParts: [],
      bytesUploaded: 0,
      status: 'uploading',
      tabLockUntil: null,
      warnOnExistingRouteXml: !!warnOnExistingRouteXml,
      existingXmlAction: existingXmlAction || '',
    }).catch(() => {});

    const accumParts = [];
    const session = { s3UploadId, s3Key, partSize, numParts, completedParts: [], fileIndex: index };
    const { completedParts } = await uploadMultipart({
      file,
      session,
      onProgress: ({ loadedBytes, totalBytes }) => trackProgress(index, loadedBytes, totalBytes, { capPct: 90 }),
      onPartComplete: (part) => {
        accumParts.push(part);
        scheduleUpdate(localId, { completedParts: [...accumParts] });
      },
      awaitPause: () => awaitPause(index),
      isCancelled: () => cancelledFilesRef.current.has(index),
      registerXhr: (xhr) => { activeXhrsRef.current.push(xhr); },
      unregisterXhr: (xhr) => { activeXhrsRef.current = activeXhrsRef.current.filter((x) => x !== xhr); },
      api,
    });

    setUploadStatus((prev) => ({ ...prev, [index]: 'finalizing' }));
    setUploadProgress((prev) => ({ ...prev, [index]: 95 }));

    const result = await completeMultipart({
      s3Key, s3UploadId, parts: completedParts,
      file,
      googleDriveId: currentFolderGoogleDriveId,
      existingXmlAction,
      warnOnExistingRouteXml,
      replaceExistingDriveFileIds,
      reproject: reprojectDecisionsRef.current[getFileKey(file)] || null,
      api,
    });

    setUploadProgress((prev) => ({ ...prev, [index]: 100 }));

    await deleteSession(localId).catch(() => {});
    delete sessionLocalIdsRef.current[index];

    return result;
  };

  // ── Retry wrapper with exponential backoff ──
  // NOTE: Retries are ONLY safe for operations that haven't touched the
  // remote destination yet.  Once a file has been created in Google Drive
  // (directly or via the backend POST) the backend has no idempotency
  // guard, so retrying would create a duplicate.  We therefore limit
  // retries to network-level / pre-upload failures.  Any error whose
  // message suggests the payload was already accepted (HTTP 2xx parse
  // failures, confirm-phase failures, etc.) is treated as non-retryable.
  const _isRetryableError = (err) => {
    if (!err) return false;
    if (err?.type === 'route_xml_conflict') return false;
    const msg = String(err.message || '').toLowerCase();
    // Errors that indicate the file already reached the server
    if (msg.includes('non-json response')) return false;   // 200/201 but bad body
    if (msg.includes('confirm failed')) return false;       // drive-direct confirm
    if (msg.includes('complete failed')) return false;      // S3→Drive complete
    if (msg.includes('complete-multipart failed')) return false;
    // Network errors before the payload was sent are safe to retry
    if (msg.includes('network error')) return true;
    // S3 pre-upload init failures are safe to retry
    if (msg.includes('init failed')) return true;
    if (msg.includes('presign failed')) return true;
    if (msg.includes('drive init failed')) return true;
    // Default: do NOT retry — the upload may have succeeded on the server
    return false;
  };

  const withRetry = async (fn, maxRetries = 2) => {
    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (err) {
        lastError = err;
        if (err?.type === 'route_xml_conflict') throw err;
        if (!_isRetryableError(err)) throw err;
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt + 1) * 1000; // 2s, 4s
          await new Promise(r => setTimeout(r, delay));
        }
      }
    }
    throw lastError;
  };

  const inspectExistingRouteXmlConflicts = async (uploadEntries) => {
    if (
      !warnOnExistingRouteXml
      || !projectId
      || !currentFolderGoogleDriveId
      || !Array.isArray(uploadEntries)
      || uploadEntries.length === 0
    ) {
      return null;
    }

    const token = await getAuthToken();
    const conflicts = [];

    for (const entry of uploadEntries) {
      const formData = new FormData();
      formData.append('file', entry.file);
      formData.append('upload_target', 'google_drive');
      formData.append('google_drive_id', currentFolderGoogleDriveId);
      formData.append('check_existing_route_xml', '1');

      try {
        const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/upload/conflicts`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload) {
          console.warn('Upload conflict inspection failed:', entry.file?.name, payload || response.statusText);
          continue;
        }

        const conflictingXmlNames = Array.isArray(payload.conflictingXmlNames)
          ? payload.conflictingXmlNames.filter(Boolean)
          : [];

        if (payload.requiresConfirmation && conflictingXmlNames.length > 0) {
          conflicts.push({
            ...entry,
            fileType: String(payload.fileType || '').trim().toLowerCase(),
            generatedXmlNames: Array.isArray(payload.generatedXmlNames)
              ? payload.generatedXmlNames.filter(Boolean)
              : [],
            conflictingXmlNames,
          });
        }
      } catch (error) {
        console.warn(`Failed checking XML conflicts for ${entry.file?.name}:`, error);
      }
    }

    if (conflicts.length === 0) {
      return null;
    }

    return buildExistingXmlConflictState(conflicts, uploadEntries);
  };

  // ── Upload a single file via the original direct POST (for small files / S3 targets) ──
  const uploadSmallFile = (file, index, existingXmlAction, replaceExistingDriveFileIds) => {
    return new Promise(async (resolve, reject) => {
      const localId = newLocalId();
      sessionLocalIdsRef.current[index] = localId;
      await saveSession({
        localId,
        projectId: String(projectId),
        folderGoogleDriveId: currentFolderGoogleDriveId || null,
        folderName: currentFolderName || projectName || 'Project Root',
        sourceStorage,
        fileName: file.name,
        fileSize: file.size,
        lastModified: file.lastModified,
        mimeType: file.type || 'application/octet-stream',
        strategy: 'small',
        sessionId: {},
        partSize: file.size,
        completedParts: [],
        bytesUploaded: 0,
        status: 'uploading',
        tabLockUntil: null,
        warnOnExistingRouteXml: !!warnOnExistingRouteXml,
        existingXmlAction: existingXmlAction || '',
      }).catch(() => {});

      const formData = new FormData();
      formData.append('file', file);
      const isDriveTarget = !!currentFolderGoogleDriveId;
      if (isDriveTarget) {
        formData.append('upload_target', 'google_drive');
        formData.append('google_drive_id', currentFolderGoogleDriveId);
        if (warnOnExistingRouteXml) {
          formData.append('check_existing_route_xml', '1');
        }
      } else {
        formData.append('parent_id', currentFolderId || projectId);
      }
      if (existingXmlAction) {
        formData.append('existing_xml_action', existingXmlAction);
      }
      if (Array.isArray(replaceExistingDriveFileIds) && replaceExistingDriveFileIds.length > 0) {
        formData.append('replace_existing_drive_file_ids', JSON.stringify(replaceExistingDriveFileIds));
      }

      const xhr = new XMLHttpRequest();
      xhr._fileIndex = index;
      activeXhrsRef.current.push(xhr);
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          trackProgress(index, e.loaded, e.total);
        }
      });

      xhr.onload = () => {
        activeXhrsRef.current = activeXhrsRef.current.filter(x => x !== xhr);
        const response = parseXhrJson(xhr);
        if (xhr.status === 409 && response?.conflictPlan) {
          setUploadStatus(prev => ({ ...prev, [index]: 'pending' }));
          setUploadProgress(prev => ({ ...prev, [index]: 0 }));
          reject({ type: 'route_xml_conflict', payload: response });
          return;
        }
        if (xhr.status === 200 || xhr.status === 201) {
          if (!response) {
            setUploadStatus(prev => ({ ...prev, [index]: 'error' }));
            reject(new Error(`Upload succeeded with non-JSON response (${xhr.status})`));
            return;
          }
          const wasSkipped = Boolean(response?.skipped);
          setUploadStatus(prev => ({ ...prev, [index]: wasSkipped ? 'skipped' : 'success' }));
          setUploadProgress(prev => ({ ...prev, [index]: wasSkipped ? 0 : 100 }));
          deleteSession(localId).catch(() => {});
          delete sessionLocalIdsRef.current[index];
          resolve(response);
        } else {
          setUploadStatus(prev => ({ ...prev, [index]: 'error' }));
          console.error('Upload HTTP error details:', {
            status: xhr.status,
            statusText: xhr.statusText,
            responseURL: xhr.responseURL,
            responseSnippet: String(xhr.responseText || '').slice(0, 400),
            responseHeaders: xhr.getAllResponseHeaders(),
          });
          reject(buildUploadError(xhr));
        }
      };

      xhr.onerror = () => {
        activeXhrsRef.current = activeXhrsRef.current.filter(x => x !== xhr);
        setUploadStatus(prev => ({ ...prev, [index]: 'error' }));
        reject(new Error('Network error'));
      };

      xhr.onabort = () => {
        activeXhrsRef.current = activeXhrsRef.current.filter(x => x !== xhr);
        reject(new Error('Upload cancelled'));
      };

      xhr.open('POST', `${API_BASE_URL}/api/projects/${projectId}/upload`);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Accept', 'application/json');
      const token = await getAuthToken();
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formData);
    });
  };

  const executeUpload = async (uploadEntries, options = {}) => {
    if (!Array.isArray(uploadEntries) || uploadEntries.length === 0) {
      return;
    }

    // LAS/LAZ reprojection preflight. Runs once per batch (the modal's Continue
    // re-enters with reprojectChecked:true). Covers every entry path into
    // executeUpload uniformly.
    if (!options.reprojectChecked) {
      if (await inspectReprojection(uploadEntries)) return;
    }

    const existingXmlAction = String(options.existingXmlAction || '').trim().toLowerCase();
    setUploading(true);
    uploadCancelledRef.current = false;
    uploadedDriveFilesRef.current = [];
    activeXhrsRef.current = [];
    activeWorkersRef.current = [];
    const results = [];
    let requiresExplorerRefresh = false;
    let shouldSignalRsaCacheSync = false;

    const signalRsaCacheSync = () => {
      if (!projectId) return;
      const syncedAt = Date.now();
      try {
        sessionStorage.setItem(`rsaMileageCachesSynced:${projectId}`, String(syncedAt));
      } catch (e) {
        // ignore storage errors
      }
      window.dispatchEvent(new CustomEvent('rsa-mileage-caches-synced', {
        detail: { projectId, syncedAt },
      }));
    };

    // Process a single upload entry (picks large vs small path, wraps in retry)
    const processEntry = async (entry, position) => {
      if (uploadCancelledRef.current) {
        return { file: entry.file.name, success: false, skipped: false, error: 'Cancelled', entry, position };
      }
      const { file, index } = entry;
      const replaceIds = entry.replaceExistingDriveFileIds || null;
      setUploadStatus(prev => ({ ...prev, [index]: 'uploading' }));
      setUploadProgress(prev => ({ ...prev, [index]: 0 }));

      try {
        const strategy = getUploadStrategy(file);

        let uploadFn;
        let retries = 2;
        if (strategy === 'visualization') {
          // Visualization upload pipeline — dedicated endpoint, separate
          // helper, no shared retry/conflict-detection logic. Don't add
          // branches here; if visualization behavior needs to change, edit
          // only uploadVisualization.js and /upload/visualization.
          uploadFn = async () => uploadVisualizationFile({
            file,
            parentId: currentFolderId || projectId,
            onProgress: ({ loadedBytes, totalBytes }) => trackProgress(index, loadedBytes, totalBytes),
            registerXhr: (xhr) => { activeXhrsRef.current.push(xhr); },
            unregisterXhr: (xhr) => { activeXhrsRef.current = activeXhrsRef.current.filter((x) => x !== xhr); },
            api: { apiBaseUrl: API_BASE_URL, projectId, authToken: await getAuthToken() },
          });
          retries = 0; // backend insert is non-idempotent; don't retry past it
        } else if (strategy === 'drive-direct') {
          // Direct-to-Drive: worker handles its own retries internally.
          // Only fall back to S3 path if Drive *init* fails (file never
          // reached Drive).  Once the worker starts uploading chunks the
          // file may already exist in Drive, so retrying via S3 would
          // create a duplicate.
          uploadFn = async () => {
            try {
              return await uploadFileDriveDirect(file, index, replaceIds);
            } catch (driveErr) {
              const msg = String(driveErr.message || '').toLowerCase();
              const isInitFailure = msg.includes('drive init failed') || msg.includes('init failed');
              if (isInitFailure) {
                console.warn(`Drive init failed for ${file.name}, falling back to S3 path:`, driveErr.message);
                setUploadProgress((prev) => ({ ...prev, [index]: 0 }));
                return uploadMultipartFile(file, index, existingXmlAction, replaceIds);
              }
              // Worker started uploading — file may already be in Drive.
              // Do NOT fall back to S3 to avoid duplicates.
              throw driveErr;
            }
          };
          retries = 0; // no retries — file may already be in Drive
        } else if (strategy === 'multipart') {
          uploadFn = () => uploadMultipartFile(file, index, existingXmlAction, replaceIds);
          retries = 1;
        } else {
          // Small file POST — the backend creates the Drive file before
          // responding, so a retry after a network error on the response
          // would create a duplicate.  Only retry on pre-send failures.
          uploadFn = () => uploadSmallFile(file, index, existingXmlAction, replaceIds);
          retries = 0;
        }

        const result = await withRetry(uploadFn, retries);

        const wasSkipped = Boolean(result?.skipped);
        setUploadStatus(prev => ({ ...prev, [index]: wasSkipped ? 'skipped' : 'success' }));
        setUploadProgress(prev => ({ ...prev, [index]: wasSkipped ? 0 : 100 }));

        if (!wasSkipped && result?.file) {
          uploadedDriveFilesRef.current.push(result.file);
        }

        return { file: file.name, success: !wasSkipped, skipped: wasSkipped, result, entry, position };

      } catch (error) {
        if (error?.type === 'route_xml_conflict') {
          return { file: file.name, success: false, skipped: false, conflict: error, entry, position };
        }

        // User-initiated cancel: keep status='cancelled' that was set when the
        // × button was clicked; don't overwrite with 'error'.
        if (cancelledFilesRef.current.has(index)) {
          console.info(`Upload cancelled by user for ${file.name}`);
          return { file: file.name, success: false, skipped: false, cancelled: true, entry, position };
        }

        console.error(`Error uploading ${file.name}:`, error, error?.message);
        setUploadStatus(prev => ({ ...prev, [index]: 'error' }));
        return { file: file.name, success: false, skipped: false, error: error?.message, entry, position };
      }
    };

    // Run uploads with concurrency limit of 2
    const CONCURRENCY = 3;
    let nextIdx = 0;
    let conflictEncountered = false;

    const runNext = async () => {
      while (nextIdx < uploadEntries.length && !conflictEncountered && !uploadCancelledRef.current) {
        const position = nextIdx++;
        const entry = uploadEntries[position];
        const outcome = await processEntry(entry, position);
        results.push(outcome);

        // Handle conflict: pause everything and show conflict dialog
        if (outcome.conflict) {
          conflictEncountered = true;
          const conflictPlan = outcome.conflict?.payload?.conflictPlan || {};
          setUploading(false);
          setExistingXmlConflictState(buildExistingXmlConflictState([
            {
              ...outcome.entry,
              fileType: String(conflictPlan.fileType || '').trim().toLowerCase(),
              generatedXmlNames: Array.isArray(conflictPlan.generatedXmlNames)
                ? conflictPlan.generatedXmlNames.filter(Boolean)
                : [],
              conflictingXmlNames: Array.isArray(conflictPlan.conflictingXmlNames)
                ? conflictPlan.conflictingXmlNames.filter(Boolean)
                : [],
            },
          ], uploadEntries.slice(outcome.position)));
          return; // stop this worker
        }

        if (outcome.success && !outcome.skipped) {
          if (outcome.result?.dxf_processing?.triggered) {
            requiresExplorerRefresh = true;
            if (Number(outcome.result?.dxf_processing?.generatedXmlCount || 0) > 0) {
              shouldSignalRsaCacheSync = true;
            }
          }
          if (outcome.result?.cache_updates) {
            shouldSignalRsaCacheSync = true;
          }
          if (outcome.result?.file && onFileAdded) {
            onFileAdded(outcome.result.file);
          }
        }
      }
    };

    // Launch up to CONCURRENCY workers
    const workers = [];
    for (let i = 0; i < Math.min(CONCURRENCY, uploadEntries.length); i++) {
      workers.push(runNext());
    }
    await Promise.all(workers);

    // If we bailed out for a conflict dialog, don't finalize
    if (conflictEncountered) return;

    setUploading(false);

    const successCount = results.filter(r => r.success).length;
    const uploadedFiles = results
      .filter((r) => r.success && !r.skipped)
      .map((r) => ({
        name: r?.entry?.file?.name || r?.file || '',
        size: Number(r?.entry?.file?.size || 0),
      }))
      .filter((f) => f.name);
    const skippedCount = results.filter(r => r.skipped).length;
    const errorCount = results.filter(r => !r.success && !r.skipped).length;
    const shouldRefreshExplorer = requiresExplorerRefresh && typeof onUpload === 'function';
    if (successCount > 0 && shouldSignalRsaCacheSync) {
      signalRsaCacheSync();
    }

    if (errorCount === 0) {
      if (successCount > 0) {
        if (shouldRefreshExplorer) {
          await Promise.resolve(onUpload?.({ uploadedFiles }));
        } else if (!onFileAdded) {
          await Promise.resolve(onUpload?.({ uploadedFiles }));
        }
      }
      handleClose(true);
    } else {
      const skippedMessage = skippedCount > 0 ? ` ${skippedCount} skipped.` : '';
      alert(`Uploaded ${successCount} file(s), ${errorCount} failed.${skippedMessage} Check console for details.`);
      if (successCount > 0 && (shouldRefreshExplorer || !onFileAdded)) {
        await Promise.resolve(onUpload?.({ uploadedFiles }));
      }
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one file to upload');
      return;
    }

    const uploadEntries = selectedFiles.map((file, index) => ({
      file,
      index,
      fileKey: getFileKey(file),
    }));

    // Fresh batch — drop any reproject decisions from a previous attempt.
    reprojectDecisionsRef.current = {};

    // Run name-collision preflight first. If it shows a dialog, bail out;
    // the user's Continue handler will resume via proceedAfterNameConflicts.
    if (await inspectNameConflicts(uploadEntries)) {
      return;
    }
    await proceedAfterNameConflicts(uploadEntries);
  };

  const handleReplaceExistingXml = async () => {
    const uploadEntries = existingXmlConflictState?.pendingUploadEntries?.length
      ? existingXmlConflictState.pendingUploadEntries
      : selectedFiles.map((file, index) => ({
          file,
          index,
          fileKey: getFileKey(file),
        }));
    setExistingXmlConflictState(null);
    await executeUpload(uploadEntries, { existingXmlAction: 'replace' });
  };

  const handleSkipExistingXml = async () => {
    const baseUploadEntries = existingXmlConflictState?.pendingUploadEntries?.length
      ? existingXmlConflictState.pendingUploadEntries
      : selectedFiles.map((file, index) => ({
          file,
          index,
          fileKey: getFileKey(file),
        }));
    const conflictFileKeys = new Set(existingXmlConflictState?.conflictFileKeys || []);
    if (conflictFileKeys.size === 0) {
      setExistingXmlConflictState(null);
      return;
    }

    setUploadStatus((prev) => {
      const next = { ...prev };
      selectedFiles.forEach((file, index) => {
        if (conflictFileKeys.has(getFileKey(file))) {
          next[index] = 'skipped';
        }
      });
      return next;
    });

    const uploadEntries = baseUploadEntries
      .filter((entry) => !conflictFileKeys.has(entry.fileKey));

    setExistingXmlConflictState(null);
    if (uploadEntries.length === 0) {
      handleClose(true);
      return;
    }

    await executeUpload(uploadEntries, { existingXmlAction: 'skip' });
  };

  const setRowAction = (index, action) => {
    handleNameConflictChoice(index, action);
  };

  const applyBulkAction = (action) => {
    if (!nameConflictState) return;
    const rows = nameConflictState.rows.map((r, i) => {
        // Don't force "replace" onto same-batch later-duplicates — fall back to "rename".
        if (action === 'replace') {
          const firstIdx = nameConflictState.rows.findIndex(
            (other) => normalizeNameForCompare(other.fileName) === normalizeNameForCompare(r.fileName)
          );
          if (firstIdx !== i) return { ...r, action: 'rename', resolved: true };
        }
        return { ...r, action, resolved: true };
      });
    handleNameConflictContinue(rows);
  };

  const handleNameConflictChoice = (index, action) => {
    if (!nameConflictState) return;
    const rows = nameConflictState.rows.map((row, i) => (
      i === index ? { ...row, action, resolved: true } : row
    ));

    if (rows.every((row) => row.resolved)) {
      handleNameConflictContinue(rows);
      return;
    }

    setNameConflictState({ ...nameConflictState, rows });
  };

  // ── LAS/LAZ reprojection preflight (S3-source projects only) ──
  // Fetch the project's registered target CRS (meters WKT) from the registry.
  const fetchProjectTargetWkt = async () => {
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${API_BASE_URL}/api/google-drive/rsa/registry/setup?project_id=${encodeURIComponent(projectId)}`,
        {
          credentials: 'include',
          headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        },
      );
      if (!res.ok) return null;
      const data = await res.json().catch(() => null);
      const wkt = data?.setup?.wkt;
      return (typeof wkt === 'string' && wkt.trim()) ? wkt.trim() : null;
    } catch (e) {
      return null;
    }
  };

  const _wktLabel = (wkt) => {
    const m = String(wkt || '').match(/PROJC(?:S|RS)\["([^"]+)"/);
    return m ? m[1] : '';
  };

  // Returns true if the upload should halt here (a modal was shown, or the
  // project is missing a CRS); false to proceed with executeUpload. Records
  // auto-detected reproject decisions into reprojectDecisionsRef.
  const inspectReprojection = async (uploadEntries) => {
    if (sourceStorage !== 's3') return false;
    const lasEntries = uploadEntries.filter((e) => isLasLaz(e.file?.name));
    if (lasEntries.length === 0) return false;

    const detected = [];
    for (const entry of lasEntries) {
      let detect;
      try {
        const header = await parseLasHeaderFromFile(entry.file);
        detect = detectUnits(header);
      } catch (e) {
        detect = { needsReproject: false, canAuto: false, reason: 'Could not read LAS header' };
      }
      detected.push({ entry, name: entry.file.name, detect });
    }

    const need = detected.filter((d) => d.detect.needsReproject);
    if (need.length === 0) return false; // everything already in meters

    const targetWkt = await fetchProjectTargetWkt();
    if (!targetWkt) {
      setReprojectModal({
        open: true,
        mode: 'blocked',
        blockedFiles: need.map((d) => ({ name: d.name, detect: d.detect })),
        pendingEntries: uploadEntries,
      });
      return true;
    }

    const autoFiles = need.filter((d) => d.detect.canAuto);
    const askFiles = need.filter((d) => !d.detect.canAuto);

    autoFiles.forEach((d) => {
      reprojectDecisionsRef.current[getFileKey(d.entry.file)] = {
        sourceCrs: d.detect.sourceCrs,
        targetWkt,
      };
    });

    if (askFiles.length === 0) return false; // proceed; auto decisions recorded

    setReprojectModal({
      open: true,
      mode: 'ask',
      askFiles: askFiles.map((d) => ({ name: d.name, detect: d.detect })),
      autoFiles: autoFiles.map((d) => ({ name: d.name, detect: d.detect })),
      askEntries: askFiles.map((d) => d.entry),
      targetWkt,
      targetLabel: _wktLabel(targetWkt),
      pendingEntries: uploadEntries,
    });
    return true;
  };

  const handleReprojectCancel = () => {
    setReprojectModal(null);
    setUploading(false);
    setCheckingConflicts(false);
  };

  const handleReprojectContinue = ({ sourceCrs }) => {
    const state = reprojectModal;
    setReprojectModal(null);
    if (!state) return;
    (state.askEntries || []).forEach((entry) => {
      reprojectDecisionsRef.current[getFileKey(entry.file)] = {
        sourceCrs,
        targetWkt: state.targetWkt,
      };
    });
    executeUpload(state.pendingEntries, { reprojectChecked: true });
  };

  const proceedAfterNameConflicts = async (uploadEntries) => {
    if (!uploadEntries || uploadEntries.length === 0) {
      setUploading(false);
      setCheckingConflicts(false);
      return;
    }
    if (warnOnExistingRouteXml && projectId && currentFolderGoogleDriveId) {
      setCheckingConflicts(true);
      try {
        const conflictState = await inspectExistingRouteXmlConflicts(uploadEntries);
        if (conflictState) {
          setExistingXmlConflictState(conflictState);
          setCheckingConflicts(false);
          return;
        }
      } finally {
        setCheckingConflicts(false);
      }
    }
    await executeUpload(uploadEntries);
  };

  const handleNameConflictContinue = (rowsOverride) => {
    if (!nameConflictState) return;
    const resolvedRows = rowsOverride || nameConflictState.rows;
    // Decisions are queued by name in insertion order; same-batch dupes have
    // their own row each, so we pop one per match.
    const queueByName = new Map();
    resolvedRows.forEach((row) => {
      const norm = normalizeNameForCompare(row.fileName);
      if (!queueByName.has(norm)) queueByName.set(norm, []);
      queueByName.get(norm).push(row);
    });

    const baseEntries = nameConflictState.pendingUploadEntries || [];
    const transformed = [];
    for (const entry of baseEntries) {
      const norm = normalizeNameForCompare(entry.file?.name);
      const queue = queueByName.get(norm);
      const decision = queue && queue.length > 0 ? queue.shift() : null;
      if (!decision) {
        transformed.push(entry);
        continue;
      }
      if (decision.action === 'skip') continue;
      if (decision.action === 'rename') {
        const wrapped = new File([entry.file], decision.newName, {
          type: entry.file.type,
          lastModified: entry.file.lastModified,
        });
        transformed.push({
          ...entry,
          file: wrapped,
          fileKey: getFileKey(wrapped),
        });
        continue;
      }
      if (decision.action === 'replace') {
        transformed.push({
          ...entry,
          replaceExistingDriveFileIds: decision.existingDriveFileIds,
        });
        continue;
      }
    }

    setNameConflictState(null);
    proceedAfterNameConflicts(transformed);
  };

  const handleCancelUpload = () => {
    if (!uploading) {
      handleClose();
      return;
    }
    setShowCancelConfirm(true);
  };

  const handleConfirmCancel = async () => {
    setShowCancelConfirm(false);
    setCancellingUpload(true);
    uploadCancelledRef.current = true;

    // Abort all active XHR requests
    const xhrs = [...activeXhrsRef.current];
    activeXhrsRef.current = [];
    xhrs.forEach(xhr => {
      try { xhr.abort(); } catch (_) { /* ignore */ }
    });

    // Abort all active Web Workers (drive-direct uploads)
    const workers = [...activeWorkersRef.current];
    activeWorkersRef.current = [];
    workers.forEach(worker => {
      try { worker.postMessage({ type: 'abort' }); } catch (_) { /* ignore */ }
      try { worker.terminate(); } catch (_) { /* ignore */ }
    });

    // Delete any persisted IndexedDB session records for in-flight uploads
    const idsToDelete = Object.values(sessionLocalIdsRef.current);
    sessionLocalIdsRef.current = {};
    await Promise.all(idsToDelete.map((id) => deleteSession(id).catch(() => {})));

    // Delete already uploaded files from Google Drive
    const uploadedFiles = [...uploadedDriveFilesRef.current];
    uploadedDriveFilesRef.current = [];

    if (uploadedFiles.length > 0 && projectId && currentFolderGoogleDriveId) {
      try {
        const token = await getAuthToken();
        const items = uploadedFiles.map(f => ({
          id: f.id || null,
          googleDriveId: f.googleDriveId || f.google_drive_id || null,
          name: f.name || null,
          isFolder: false,
          folder_drive_id: currentFolderGoogleDriveId,
        }));

        await fetch(`${API_BASE_URL}/api/projects/${projectId}/google-drive-files/delete`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            folder_drive_id: currentFolderGoogleDriveId,
            items,
          }),
        });
      } catch (err) {
        console.warn('Failed to clean up uploaded files after cancel:', err);
      }
    }

    setCancellingUpload(false);
    // Clean up context entry when cancelling from minimized state
    if (activeUploadIdRef.current) {
      removeContextUpload(activeUploadIdRef.current);
      closeUploadsPopup();
    }
    handleClose(true);
  };

  // Keep ref in sync so the registered closure always calls the latest version
  handleConfirmCancelRef.current = handleConfirmCancel;

  const handleDismissCancelConfirm = () => {
    setShowCancelConfirm(false);
  };

  const handleClose = (force = false) => {
    if (!force && (uploading || checkingConflicts)) {
      return;
    }
    // Clear any pending debounced persistence timers
    Object.values(debouncedUpdateRef.current).forEach((entry) => {
      if (entry?.timer) clearTimeout(entry.timer);
    });
    debouncedUpdateRef.current = {};
    sessionLocalIdsRef.current = {};
    setSelectedFiles([]);
    setUploadProgress({});
    setUploadSpeed({});
    speedTrackerRef.current = {};
    cancelledFilesRef.current = new Set();
    pausedFilesRef.current = new Set();
    // Resolve any dangling pause waiters so the upload loops can unwind.
    Object.values(pauseWaitersRef.current).forEach((arr) => {
      arr.forEach((r) => { try { r(); } catch { /* ignore */ } });
    });
    pauseWaitersRef.current = {};
    setUploadStatus({});
    setUploading(false);
    setCheckingConflicts(false);
    setIsDragActive(false);
    setExistingXmlConflictState(null);
    setShowCancelConfirm(false);
    setCancellingUpload(false);
    setMinimized(false);
    if (activeUploadIdRef.current) {
      unregisterCancelHandler(activeUploadIdRef.current);
      unregisterExpandHandler(activeUploadIdRef.current);
    }
    activeUploadIdRef.current = null;
    uploadCancelledRef.current = false;
    uploadedDriveFilesRef.current = [];
    activeXhrsRef.current = [];
    activeWorkersRef.current.forEach(w => { try { w.terminate(); } catch (_) { /* ignore */ } });
    activeWorkersRef.current = [];
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Tracks cumulative bytes over time and emits a rolling MB/s estimate plus
  // a 0-100 percentage. Percentage updates every tick (cheap); speed is
  // resampled on a 1.7-second interval so the displayed number is stable
  // instead of jittery.
  const SPEED_SAMPLE_INTERVAL_MS = 1700;
  const trackProgress = (index, loadedBytes, totalBytes, { capPct = 100 } = {}) => {
    const pct = totalBytes > 0
      ? Math.min(capPct, Math.round((loadedBytes / totalBytes) * capPct))
      : 0;
    setUploadProgress((prev) => (prev[index] === pct ? prev : { ...prev, [index]: pct }));

    const now = Date.now();
    const prev = speedTrackerRef.current[index];
    if (!prev) {
      speedTrackerRef.current[index] = { lastBytes: loadedBytes, lastTime: now };
      return;
    }
    if (now - prev.lastTime < SPEED_SAMPLE_INTERVAL_MS) return; // throttle
    const dBytes = Math.max(0, loadedBytes - prev.lastBytes);
    const dSec = (now - prev.lastTime) / 1000;
    if (dSec <= 0) return;
    const mbps = dBytes / 1024 / 1024 / dSec;
    speedTrackerRef.current[index] = { lastBytes: loadedBytes, lastTime: now };
    setUploadSpeed((prevSpeed) => ({ ...prevSpeed, [index]: mbps }));
  };

  // Aborts all in-flight XHRs belonging to this file index. Leaves other
  // files' uploads running. Marks status='cancelled' so the processEntry
  // catch block does not overwrite it with 'error'.
  const handleCancelFileUpload = (index) => {
    cancelledFilesRef.current.add(index);
    // Resolve any pending pause-waiters so the multipart loop can run to its
    // next check, see the cancel flag, and bail out cleanly.
    const waiters = pauseWaitersRef.current[index];
    if (waiters && waiters.length) {
      waiters.forEach((r) => { try { r(); } catch { /* ignore */ } });
      pauseWaitersRef.current[index] = [];
    }
    pausedFilesRef.current.delete(index);
    const xhrs = activeXhrsRef.current.filter((x) => x && x._fileIndex === index);
    xhrs.forEach((x) => { try { x.abort(); } catch { /* ignore */ } });
    setUploadStatus((prev) => ({ ...prev, [index]: 'cancelled' }));
    setUploadProgress((prev) => ({ ...prev, [index]: 0 }));
    setUploadSpeed((prev) => ({ ...prev, [index]: 0 }));
    delete speedTrackerRef.current[index];
  };

  // Between multipart parts the upload loop awaits this. If the file is not
  // paused it resolves immediately. If paused, it parks a resolver that will
  // be called by handleResumeFileUpload to unblock.
  const awaitPause = (index) => {
    if (!pausedFilesRef.current.has(index)) return Promise.resolve();
    return new Promise((resolve) => {
      if (!pauseWaitersRef.current[index]) pauseWaitersRef.current[index] = [];
      pauseWaitersRef.current[index].push(resolve);
    });
  };

  // Signal "hold before starting the next multipart part". Does not abort the
  // current in-flight part (that would waste the bytes already uploaded) —
  // the pause takes effect between parts. For very small files that use the
  // presigned or direct strategies there is no per-part granularity, so pause
  // is effectively a no-op until the current XHR finishes; the next file in
  // the queue then waits on its own awaitPause.
  const handlePauseFileUpload = (index) => {
    pausedFilesRef.current.add(index);
    setUploadStatus((prev) => ({ ...prev, [index]: 'paused' }));
    setUploadSpeed((prev) => ({ ...prev, [index]: 0 }));
    delete speedTrackerRef.current[index];
  };

  const handleResumeFileUpload = (index) => {
    pausedFilesRef.current.delete(index);
    setUploadStatus((prev) => ({ ...prev, [index]: 'uploading' }));
    const waiters = pauseWaitersRef.current[index];
    if (waiters && waiters.length) {
      waiters.forEach((r) => { try { r(); } catch { /* ignore */ } });
      pauseWaitersRef.current[index] = [];
    }
  };

  const formatMbps = (mbps) => {
    if (mbps == null || !isFinite(mbps) || mbps <= 0) return '';
    if (mbps >= 10) return `${mbps.toFixed(0)} MB/s`;
    if (mbps >= 1)  return `${mbps.toFixed(1)} MB/s`;
    return `${(mbps * 1024).toFixed(0)} KB/s`;
  };

  // Coarse, low-precision ETA. Avoids second-by-second jitter at small
  // values and avoids fake precision at large ones. Returns '' when no
  // meaningful ETA can be shown (caller hides the pill).
  const formatEta = (seconds) => {
    if (seconds == null || !isFinite(seconds) || seconds <= 0) return '';
    if (seconds < 10) return '<10s';
    if (seconds < 60) return `~${Math.ceil(seconds)}s`;
    if (seconds < 3600) return `~${Math.round(seconds / 60)}m`;
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return m ? `~${h}h ${m}m` : `~${h}h`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return 'fas fa-check-circle success';
      case 'error':
        return 'fas fa-times-circle error';
      case 'uploading':
        return 'fas fa-spinner fa-spin uploading';
      case 'finalizing':
        return 'fas fa-spinner fa-spin finalizing';
      case 'skipped':
        return 'fas fa-forward skipped';
      case 'paused':
        return 'fa-solid fa-pause paused';
      case 'cancelled':
        return 'fa-solid fa-ban cancelled';
      default:
        return 'fas fa-clock pending';
    }
  };

  const totalSelectedBytes = selectedFiles.reduce((sum, file) => sum + (file.size || 0), 0);
  const modalBusy = uploading || checkingConflicts;
  const conflictXmlNames = existingXmlConflictState?.existingXmlNames || [];
  const visibleConflictNames = conflictXmlNames.slice(0, 4);
  const hasMoreConflictNames = conflictXmlNames.length > visibleConflictNames.length;
  const hasDxfConflict = (existingXmlConflictState?.conflicts || []).some((entry) => entry.fileType === 'dxf');
  const replaceLabel = conflictXmlNames.length === 1
    ? 'Replace the file in the destination'
    : 'Replace the files in the destination';
  const skipLabel = conflictXmlNames.length === 1
    ? 'Skip this file'
    : 'Skip these files';

  if (minimized) {
    return null;
  }

  return (
    <div className="upload-modal-overlay" onClick={() => handleClose()}>
      <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
        {reprojectModal?.open && (
          <ReprojectCrsModal
            open={reprojectModal.open}
            mode={reprojectModal.mode}
            askFiles={reprojectModal.askFiles || []}
            autoFiles={reprojectModal.autoFiles || []}
            blockedFiles={reprojectModal.blockedFiles || []}
            targetLabel={reprojectModal.targetLabel || ''}
            onCancel={handleReprojectCancel}
            onContinue={handleReprojectContinue}
          />
        )}
        {nameConflictState && (
          <div className="upload-conflict-overlay" onClick={() => setNameConflictState(null)}>
            <div
              className="upload-conflict-modal name-conflict-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="upload-conflict-header">
                <div>
                  <h3 className="upload-conflict-title">
                    {nameConflictState.rows.length === 1 ? 'Replace or Skip File' : 'Replace or Skip Files'}
                  </h3>
                </div>
                <button
                  type="button"
                  className="upload-conflict-close"
                  onClick={() => setNameConflictState(null)}
                  aria-label="Close conflict dialog"
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>

              <p className="name-conflict-subtitle">
                {nameConflictState.rows.length === 1 ? 'The destination already has a file named' : 'The destination already has files with these names'}
              </p>

              {nameConflictState.rows.length > 1 && (
                <div className="name-conflict-bulk-actions">
                  <span>Apply to all:</span>
                  <button type="button" onClick={() => applyBulkAction('rename')}>Keep both</button>
                  <button type="button" onClick={() => applyBulkAction('replace')}>Replace</button>
                  <button type="button" onClick={() => applyBulkAction('skip')}>Skip</button>
                </div>
              )}

              <ul className="name-conflict-rows">
                {nameConflictState.rows.map((row, i) => {
                  // Same-name rows beyond the first cannot meaningfully "Replace" — they would just
                  // recreate a duplicate after the first row trashed the existing file. Disable the
                  // Replace radio on rows 2+.
                  const isLaterDupe = nameConflictState.rows.findIndex(
                    (r) => normalizeNameForCompare(r.fileName) === normalizeNameForCompare(row.fileName)
                  ) !== i;
                  return (
                    <li key={`${row.fileName}-${i}`} className="name-conflict-row">
                      <div className="name-conflict-row-name" title={row.fileName}>"{row.fileName}"</div>
                      <div className="name-conflict-row-actions">
                        <label>
                          <input
                            type="radio"
                            name={`name-conflict-${i}`}
                            checked={row.resolved && row.action === 'rename'}
                            onChange={() => setRowAction(i, 'rename')}
                          />
                          <i className="fas fa-copy" aria-hidden="true" />
                          <span>Keep both <code>{row.newName}</code></span>
                        </label>
                        <label className={isLaterDupe ? 'name-conflict-row-disabled' : ''}>
                          <input
                            type="radio"
                            name={`name-conflict-${i}`}
                            checked={row.resolved && row.action === 'replace'}
                            onChange={() => setRowAction(i, 'replace')}
                            disabled={isLaterDupe}
                          />
                          <i className="fas fa-check" aria-hidden="true" />
                          <span>Replace</span>
                          {isLaterDupe && <span className="name-conflict-disabled-hint">Not available for duplicate rows in this batch</span>}
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`name-conflict-${i}`}
                            checked={row.resolved && row.action === 'skip'}
                            onChange={() => setRowAction(i, 'skip')}
                          />
                          <i className="fas fa-undo" aria-hidden="true" />
                          <span>Skip</span>
                        </label>
                      </div>
                    </li>
                  );
                })}
              </ul>

            </div>
          </div>
        )}

        {existingXmlConflictState && (
          <div className="upload-conflict-overlay" onClick={() => setExistingXmlConflictState(null)}>
            <div className="upload-conflict-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="upload-conflict-header">
                <h3>Replace or Skip Files</h3>
                <button
                  type="button"
                  className="upload-conflict-close"
                  onClick={() => setExistingXmlConflictState(null)}
                  aria-label="Close conflict dialog"
                >
                  <i className="fas fa-times" />
                </button>
              </div>
              <p className="upload-conflict-copy">
                {conflictXmlNames.length === 1
                  ? 'The destination already has a file named'
                  : 'The destination already has files named'}
              </p>
              <div className="upload-conflict-names">
                {visibleConflictNames.map((name) => (
                  <div key={name} className="upload-conflict-name">
                    "{name}"
                  </div>
                ))}
                {hasMoreConflictNames && (
                  <div className="upload-conflict-more">and {conflictXmlNames.length - visibleConflictNames.length} more</div>
                )}
              </div>
              {hasDxfConflict && (
                <p className="upload-conflict-note">
                  Some conflicts come from XML files that would be generated by the selected DXF upload.
                </p>
              )}
              <div className="upload-conflict-actions">
                <button type="button" className="upload-conflict-action upload-conflict-action-primary" onClick={handleReplaceExistingXml}>
                  <i className="fas fa-check" />
                  <span>{replaceLabel}</span>
                </button>
                <button type="button" className="upload-conflict-action" onClick={handleSkipExistingXml}>
                  <i className="fas fa-forward" />
                  <span>{skipLabel}</span>
                </button>
              </div>
            </div>
          </div>
        )}
        {showCancelConfirm && (
          <div className="upload-cancel-confirm-overlay" onClick={handleDismissCancelConfirm}>
            <div className="upload-cancel-confirm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="upload-cancel-confirm-header">
                <i className="fa-solid fa-triangle-exclamation upload-cancel-confirm-warn-icon"></i>
                <h3>Cancel Upload?</h3>
              </div>
              <p className="upload-cancel-confirm-copy">
                Are you sure you want to cancel? All files will be removed, including those already uploaded.
              </p>
              <div className="upload-cancel-confirm-actions">
                <button type="button" className="upload-cancel-confirm-action upload-cancel-confirm-yes" onClick={handleConfirmCancel}>
                  Yes, cancel
                </button>
                <button type="button" className="upload-cancel-confirm-action upload-cancel-confirm-no" onClick={handleDismissCancelConfirm}>
                  No, continue uploading
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="upload-modal-header">
          <h2>Upload Files</h2>
          <div className="upload-modal-header-actions">
            {uploading && (
              <button className="upload-modal-minimize" onClick={handleMinimize} title="Run in background">
                <i className="fa-solid fa-compress"></i>
                <span>Run in background</span>
              </button>
            )}
            <button className="upload-modal-close" onClick={() => handleClose()}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div className="upload-modal-content">
          <div className="upload-info">
            <p>
              <i className="fas fa-folder"></i>
              Uploading to: <strong>{currentFolderName || projectName || 'Project Root'}</strong>
            </p>
          </div>

          <div
            className={`upload-file-selector ${isDragActive ? 'drag-active' : ''} ${selectedFiles.length > 0 ? 'has-files' : ''}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="upload-files-summary" aria-hidden={selectedFiles.length === 0}>
              <div className="upload-files-summary-header">
                <i className="fas fa-folder-open"></i>
                <div className="upload-files-summary-meta">
                  <span className="upload-files-summary-count">{selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}</span>
                  <span className="upload-files-summary-total">{formatFileSize(totalSelectedBytes)}</span>
                </div>
              </div>
              <div className="upload-files-summary-list">
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                    className={`upload-files-summary-item${
                      (uploadStatus[index] || 'pending') === 'paused' ? ' paused' : ''
                    }`}
                  >
                    <span className="upload-files-summary-name" title={file.name}>{file.name}</span>
                    {(() => {
                      const status = uploadStatus[index] || 'pending';
                      const pct = uploadProgress[index];
                      const mbps = uploadSpeed[index];
                      const isActive = status === 'uploading' || status === 'finalizing';
                      const isPaused = status === 'paused';
                      const canToggle = isActive || isPaused;
                      const canCancel = canToggle; // only while live/paused
                      // ETA: only meaningful while actively uploading with a real speed
                      // sample, before we cross into the finalizing flush. Hidden once
                      // pct >= 98 because the tail is dominated by commit/handshake, not
                      // bytes-on-the-wire, and any number we'd show would lie.
                      const etaSec = (status === 'uploading' && mbps > 0 && pct != null && pct < 98)
                        ? (file.size * (1 - pct / 100)) / (mbps * 1024 * 1024)
                        : null;
                      const etaLabel = etaSec != null ? formatEta(etaSec) : '';
                      return (
                        <div className="upload-files-summary-actions">
                          {/* Group 1: file state — size, percentage, terminal-state icon.
                              Spinner is intentionally omitted during 'uploading'/'finalizing'
                              because the periodic speed pill + animated progress bar already
                              signal that the upload is alive. The icon only appears once the
                              row reaches a terminal state (success/error/cancelled/skipped). */}
                          <div className="upload-files-summary-info">
                            <span className="upload-files-summary-size">{formatFileSize(file.size)}</span>
                            {(isActive || isPaused) && pct != null && (
                              <span className="upload-files-summary-pct">{pct}%</span>
                            )}
                            {(status === 'success' || status === 'error' || status === 'cancelled' || status === 'skipped') && (
                              <i className={`upload-files-summary-status-icon ${getStatusIcon(status)}`}></i>
                            )}
                          </div>

                          {/* Group 2: live metrics — speed + ETA, or paused badge */}
                          <div className="upload-files-summary-metrics">
                            {isActive && mbps > 0 && (
                              <span className="upload-files-summary-speed">{formatMbps(mbps)}</span>
                            )}
                            {etaLabel && (
                              <span className="upload-files-summary-eta">{etaLabel}</span>
                            )}
                            {isPaused && (
                              <span className="upload-files-summary-paused-label">paused</span>
                            )}
                          </div>

                          {/* Group 3: row controls — pause/resume + cancel/remove */}
                          <div className="upload-files-summary-controls">
                            {canToggle && (
                              <button
                                type="button"
                                className={`upload-files-summary-toggle ${isPaused ? 'resume' : 'pause'}`}
                                onClick={() => (isPaused ? handleResumeFileUpload(index) : handlePauseFileUpload(index))}
                                title={isPaused ? 'Resume upload' : 'Pause upload'}
                                aria-label={isPaused ? `Resume upload of ${file.name}` : `Pause upload of ${file.name}`}
                              >
                                <i className={isPaused ? 'fa-solid fa-play' : 'fa-solid fa-pause'}></i>
                              </button>
                            )}
                            <button
                              type="button"
                              className="upload-files-summary-remove"
                              onClick={() => (canCancel ? handleCancelFileUpload(index) : handleRemoveFile(index))}
                              title={canCancel ? 'Cancel this upload' : 'Remove file'}
                              aria-label={canCancel ? `Cancel upload of ${file.name}` : `Remove ${file.name}`}
                            >
                              <i className="fa-solid fa-xmark"></i>
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                    {(uploadStatus[index] === 'uploading' || uploadStatus[index] === 'finalizing' || uploadStatus[index] === 'paused') && (
                      <div className="upload-files-summary-progress">
                        <div
                          className={`upload-files-summary-progress-fill${
                            uploadStatus[index] === 'finalizing' ? ' finalizing'
                            : uploadStatus[index] === 'paused' ? ' paused'
                            : ''
                          }`}
                          style={{ width: `${uploadProgress[index] || 0}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="upload-dropzone">
              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept={accept}
                onChange={handleFileSelect}
                className="upload-file-input"
                id="file-upload-input"
                disabled={modalBusy}
              />
              <label htmlFor="file-upload-input" className="upload-file-label">
              <i className="fas fa-cloud-upload-alt"></i>
              <span>
  {isDragActive ? (
    'Drop files here'
  ) : (
    <>

      <span style={{ fontWeight: 700, fontSize: '20px' }}> Choose a file</span>
      <span style={{ fontWeight: 300, fontSize: '20px' }}> or drop it here</span>
    </>
  )}
</span>

            </label>
            </div>
          </div>
        </div>

        <div className="upload-modal-footer">
          <button className="upload-cancel-btn" onClick={handleCancelUpload} disabled={cancellingUpload}>
            Cancel
          </button>
          <button 
            className="upload-submit-btn" 
            onClick={handleUpload} 
            disabled={selectedFiles.length === 0 || modalBusy}
          >
            {checkingConflicts ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Checking...
              </>
            ) : uploading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Uploading...
              </>
            ) : (
              <>
                <i className="fas fa-upload"></i>
                Upload {selectedFiles.length > 0 ? `${selectedFiles.length} ` : ''}File{selectedFiles.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
